import React, { useState } from "react";
import { StyledContainer } from "../../../assets/styles/WebsiteLinks";
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import emed4uIcon from "../../../assets/img/Emed4u.png";
import emedKotiIcon from "../../../assets/img/Emed4u(koti)devices.png";

function PrivacyPolicy() {
  const [activeView, setActiveView] = useState("all");
  const theme = useTheme();
  // dense 3-column permission tables switch to stacked cards below "md" (phones + tablets)
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const handleSelectApp = (view, sectionId) => {
    setActiveView(view);
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  /* ── shared sx helpers ─────────────────────────────────── */
  const sectionPaper = (borderColor, bgColor) => ({
    p: { xs: 2, sm: 3, md: 4 },
    borderRadius: 3,
    border: `2px solid ${borderColor}`,
    bgcolor: bgColor,
  });

  const sectionBox = { width: "100%", mb: 4, pt: 1 };

  const h4 = (color) => ({
    fontWeight: 700,
    color,
    fontSize: { xs: "1.3rem", sm: "1.6rem", md: "2rem" },
    lineHeight: 1.3,
  });

  const h6color = (color) => ({
    fontWeight: 700,
    color,
    mt: 3,
    mb: 1,
    fontSize: { xs: "0.95rem", sm: "1.1rem" },
  });

  /* ── mobile-friendly permission card (replaces table row on xs) ── */
  const PermRow = ({ label, ids, why }) => (
    <Box sx={{ mb: 2, p: { xs: 1.25, sm: 1.5 }, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0", overflowWrap: "anywhere" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#475569", mb: 0.5, wordBreak: "break-word", fontSize: { xs: "0.68rem", sm: "0.75rem" } }}>{ids}</Typography>
      <Typography variant="body2" sx={{ color: "#334155", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>{why}</Typography>
    </Box>
  );

  return (
    <StyledContainer>
      {/* ── TITLE ─────────────────────────────────────────────── */}
      <Typography
        variant="h3"
        className="centered"
        sx={{ mb: 1, fontSize: { xs: "1.6rem", sm: "2.2rem", md: "3rem" } }}
      >
        Privacy Policy
      </Typography>

      <Typography
        variant="body1"
        className="centered"
        sx={{ color: "text.secondary", mb: 3, maxWidth: 850, width: "100%", px: { xs: 0.5, sm: 0 }, fontSize: { xs: "0.875rem", sm: "1rem" } }}
      >
        Emed4u Private Limited is committed to protecting the privacy and security of your
        personal data. Below you will find comprehensive privacy information for our web
        platform and mobile applications.
      </Typography>

      {/* ── APP SELECTOR CARDS ──────────────────────────────── */}
      <Box sx={{ width: "100%", mb: 3 }}>
        <Paper
          elevation={0}
          sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 3, mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
            Select an Application to Read Details
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
            Tap a card below to focus on its specific privacy details, permissions, and features — or browse all sections.
          </Typography>

          <Grid container spacing={2}>
            {/* KOTI CARD */}
            <Grid item xs={12} sm={6}>
              <Card
                sx={{
                  height: "100%", borderRadius: 2,
                  border: activeView === "koti" ? "2px solid #16a34a" : "1px solid #86efac",
                  bgcolor: "#f0fdf4", transition: "all 0.2s",
                  boxShadow: activeView === "koti" ? 4 : 1,
                }}
              >
                <CardActionArea
                  onClick={() => handleSelectApp(activeView === "koti" ? "all" : "koti", "emed-koti-section")}
                  sx={{ p: { xs: 1.5, sm: 2.5 }, height: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", mb: 1.5 }}>
                    <Box component="img" src={emedKotiIcon} alt="Emed(Koti)Devices"
                      sx={{ width: { xs: 52, sm: 68 }, height: { xs: 52, sm: 68 }, borderRadius: 2, boxShadow: 2, mr: 1.5, objectFit: "contain", bgcolor: "#fff", p: 0.5 }} />
                    <Box>
                      <Chip label="Companion Wearable" color="success" size="small" sx={{ mb: 0.5, fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: "#166534", fontSize: { xs: "0.95rem", sm: "1.1rem" } }}>
                        Emed(Koti)Devices
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#15803d", display: "block", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                        Smart Ring & Health Wearable
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 0, width: "100%" }}>
                    <Typography variant="body2" sx={{ color: "#334155", mb: 1.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      • <strong>Wearable:</strong> Heart Rate, SpO2, sleep, temperature.<br />
                      • <strong>Permissions:</strong> Bluetooth BLE & Location for pairing.<br />
                      • <strong>Account:</strong> Login with existing Emed4u account.
                    </Typography>
                    <Button variant={activeView === "koti" ? "contained" : "outlined"} color="success"
                      fullWidth size="small"
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                      {activeView === "koti" ? "Viewing Emed(Koti)Devices Policy ✓" : "Read Emed(Koti)Devices Policy ↓"}
                    </Button>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>

            {/* EMED4U CARD */}
            <Grid item xs={12} sm={6}>
              <Card
                sx={{
                  height: "100%", borderRadius: 2,
                  border: activeView === "emed4u" ? "2px solid #2563eb" : "1px solid #93c5fd",
                  bgcolor: "#eff6ff", transition: "all 0.2s",
                  boxShadow: activeView === "emed4u" ? 4 : 1,
                }}
              >
                <CardActionArea
                  onClick={() => handleSelectApp(activeView === "emed4u" ? "all" : "emed4u", "emed4u-section")}
                  sx={{ p: { xs: 1.5, sm: 2.5 }, height: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", mb: 1.5 }}>
                    <Box component="img" src={emed4uIcon} alt="Emed4u"
                      sx={{ width: { xs: 52, sm: 68 }, height: { xs: 52, sm: 68 }, borderRadius: 2, boxShadow: 2, mr: 1.5, objectFit: "contain", bgcolor: "#fff", p: 0.5 }} />
                    <Box>
                      <Chip label="Telemedicine App" color="primary" size="small" sx={{ mb: 0.5, fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: "#1e40af", fontSize: { xs: "0.95rem", sm: "1.1rem" } }}>
                        Emed4u
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#1d4ed8", display: "block", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                        Healthcare & Personal Health Records
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 0, width: "100%" }}>
                    <Typography variant="body2" sx={{ color: "#334155", mb: 1.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      • <strong>Telemedicine:</strong> Video consultations & appointments.<br />
                      • <strong>File Picker:</strong> System photo picker & document selector.<br />
                      • <strong>Account:</strong> Full profile & data deletion support.
                    </Typography>
                    <Button variant={activeView === "emed4u" ? "contained" : "outlined"} color="primary"
                      fullWidth size="small"
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                      {activeView === "emed4u" ? "Viewing Emed4u App Policy ✓" : "Read Emed4u App Policy ↓"}
                    </Button>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* NAV PILL BUTTONS */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", my: 2 }}>
          {[
            { label: "Show All Sections", view: "all", id: "", color: "default" },
            { label: "Emed(Koti)Devices", view: "koti", id: "emed-koti-section", color: "success" },
            { label: "Emed4u App", view: "emed4u", id: "emed4u-section", color: "primary" },
            { label: "Account & Deletion", view: "deletion", id: "account-deletion-section", color: "secondary" },
            { label: "General Legal Terms", view: "general", id: "general-legal-section", color: "inherit" },
          ].map(({ label, view, id, color }) => (
            <Button
              key={view}
              variant={activeView === view ? "contained" : "outlined"}
              color={color === "default" ? undefined : color}
              size="small"
              onClick={() => view === "all" ? setActiveView("all") : handleSelectApp(view, id)}
              sx={{ textTransform: "none", borderRadius: 20, fontSize: { xs: "0.7rem", sm: "0.8rem" }, px: { xs: 1.5, sm: 2 } }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* ── SECTION 1: EMED(KOTI)DEVICES ──────────────────────── */}
      {(activeView === "all" || activeView === "koti") && (
        <Box id="emed-koti-section" sx={sectionBox}>
          <Paper elevation={2} sx={sectionPaper("#86efac", "#fafdfb")}>

            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <Box component="img" src={emedKotiIcon} alt="Emed(Koti)Devices"
                sx={{ width: { xs: 44, sm: 56 }, height: { xs: 44, sm: 56 }, borderRadius: 2, boxShadow: 1, bgcolor: "#fff", p: 0.5, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={h4("#15803d")}>Privacy Policy — Emed(Koti)Devices</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  Companion App for Smart Rings & Wearable Health Devices
                </Typography>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
              <AlertTitle sx={{ fontWeight: 700 }}>No Sign-Up in This App</AlertTitle>
              Emed(Koti)Devices does not create new accounts. Your account is created via the{" "}
              <strong>web portal (emed4u.com)</strong> or the <strong>Emed4u app</strong>. This app is used
              strictly to <strong>log in</strong> with that existing account and connect to your paired device.
            </Alert>

            <Typography variant="h6" sx={h6color("#166534")}>Key Points (Summary)</Typography>
            <Typography variant="p">
              <ul>
                <li><strong>No sign-up in this app:</strong> Your account is created via the web portal or Emed4u app. This app only logs you in and connects to your device.</li>
                <li><strong>Minimal data collection:</strong> We collect only what is needed to pair your ring/device: Bluetooth, location (required by Android for BLE), body sensor data (heart rate, SpO2, etc.), and basic device/storage access.</li>
                <li><strong>We never sell, rent, or trade</strong> your personal or health data to third parties.</li>
                <li><strong>Data Sharing:</strong> Data may be shared only with your consent, to deliver a service you requested, or when legally required.</li>
                <li><strong>Account control:</strong> You can request account or data changes through the Emed4u app or web portal.</li>
                <li><strong>Security:</strong> We use encryption and access controls. Protect your login credentials.</li>
              </ul>
            </Typography>

            <Typography variant="h6" sx={h6color("#166534")}>1. About This Policy</Typography>
            <Typography variant="p">
              Emed(Koti)Devices ("the App") is developed and operated by Emed4u.com Pvt Ltd. We respect and protect
              the privacy of everyone who uses the App. By logging into and using the App, you agree to this Policy.
              <br /><br />
              <strong>Important:</strong> This app does not support account registration. Accounts are created and managed
              via the web portal or Emed4u app. This app is limited to:
              <ul>
                <li>Logging in with your existing account, and</li>
                <li>Connecting to, and displaying data from, your paired device (smart ring).</li>
              </ul>
            </Typography>

            <Typography variant="h6" sx={h6color("#166534")}>2. Information We Collect</Typography>
            <Typography variant="p">
              <ul>
                <li><strong>(a) Login information:</strong> Credentials/identifiers of your existing account (created via web or Emed4u), not new registration data.</li>
                <li><strong>(b) Device & usage information:</strong> Device type, OS version, app version, and basic connection/session logs for pairing.</li>
                <li><strong>(c) Health & sensor data:</strong> Data from your paired smart ring — heart rate, SpO2, ECG, sleep, steps, and temperature.</li>
                <li><strong>(d) Location data:</strong> Required by Android for BLE scanning/pairing, and for activity tracking (distance for steps/running).</li>
                <li><strong>(e) Clipboard (optional):</strong> Read only when you paste text into the App. We do not upload or store clipboard content.</li>
              </ul>
            </Typography>

            <Typography variant="h6" sx={h6color("#166534")}>3. Permissions Used by Emed(Koti)Devices</Typography>

            {/* TABLE — scrollable on mobile */}
            <TableContainer component={Paper} elevation={0}
              sx={{ my: 2, border: "1px solid #e0e0e0", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <Table size="small" sx={{ minWidth: { xs: 480, sm: "unset" } }}>
                <TableHead sx={{ bgcolor: "#f0fdf4" }}>
                  <TableRow>
                    {["Permission", "Android Identifier", "Why It's Needed"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ["Bluetooth", "BLUETOOTH, BLUETOOTH_ADMIN, BLUETOOTH_SCAN, BLUETOOTH_CONNECT", "Scan for, pair with, and sync data from your smart ring."],
                    ["Location", "ACCESS_FINE_LOCATION, ACCESS_COARSE_LOCATION", "Required by Android for BLE scanning; also used for distance tracking during walking/running."],
                    ["Body Sensors", "BODY_SENSORS", "Read heart rate, SpO2, and related health metrics from your connected device."],
                    ["Photo / Storage", "System Photo Picker / SAF", "Used only if you export or view a report image. No broad external file system access is requested."],
                  ].map(([perm, id, why]) => (
                    <TableRow key={perm}>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{perm}</TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.72rem", sm: "0.8rem" } }}><code>{id}</code></TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{why}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 2, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
              * We do not request Camera, SMS, call log, contacts, or calendar permissions, as these are not needed for this app's login-and-device-sync purpose.
            </Typography>

            <Typography variant="h6" sx={h6color("#166534")}>4. How We Use Your Information</Typography>
            <Typography variant="p">
              <ul>
                <li>To pair, connect, and sync data with your smart ring.</li>
                <li>To display your health and activity metrics inside the App.</li>
                <li>To maintain and improve App performance and troubleshoot issues.</li>
                <li>To send you relevant product/service information (only with your consent where required).</li>
              </ul>
              We do <strong>not</strong> sell, rent, share, or trade your personal information with unrelated third parties.
              Any partner who processes data on our behalf may only do so to deliver the service you requested.
            </Typography>

            <Typography variant="h6" sx={h6color("#166534")}>5. Account Management & Cancellation</Typography>
            <Typography variant="p">
              Because sign-up happens outside this app:
              <ul>
                <li>To create, edit, or cancel your account, use the <strong>web portal</strong> or the <strong>Emed4u app</strong> where the account was originally created.</li>
                <li>Logging out of Emed(Koti)Devices only ends your session on this device — it does not delete or cancel your account.</li>
                <li>If you cancel your account via the web/Emed4u app, your ability to log into Emed(Koti)Devices will end accordingly.</li>
              </ul>
            </Typography>

            {/* ── EXPANDABLE APPENDIX A ──────────────────────────── */}
            <Accordion
              elevation={0}
              sx={{ mt: 4, mb: 1, border: "1px solid #bbf7d0", borderRadius: "8px !important", "&:before": { display: "none" } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f0fdf4", borderRadius: "8px !important", minHeight: 48 }}>
                <Typography sx={{ fontWeight: 700, color: "#166534", fontSize: { xs: "0.8rem", sm: "0.95rem" } }}>
                  Appendix A: Full Device Permissions & System Features Reference (Tap to Expand)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: { xs: 1.5, sm: 3 }, bgcolor: "#fff", borderTop: "1px solid #bbf7d0" }}>

                <Typography variant="h6" sx={{ fontWeight: 700, color: "#15803d", mb: 2, fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  Authority Name, Usage Scenarios & Call Paths
                </Typography>

                {/* On phones & tablets: cards. On desktop: table */}
                {isCompact ? (
                  <Box sx={{ mb: 3 }}>
                    <PermRow
                      label="Location & Bluetooth (Sports / BLE pairing)"
                      ids="ACCESS_BACKGROUND_LOCATION · ACCESS_FINE_LOCATION · ACCESS_COARSE_LOCATION · BLUETOOTH · BLUETOOTH_ADMIN"
                      why="Calculates running distance, searches for BLE bracelet, tracks location in background, draws activity trajectory. Path: App Home → Sports tab → Running"
                    />
                    <PermRow
                      label="SMS / Calls / Notifications (Bracelet push)"
                      ids="READ_SMS · CALL_PHONE · ACCESS_NOTIFICATION_POLICY · WRITE_CONTACTS · RECEIVE_SMS"
                      why="Pushes mobile phone calls, SMS, and notification bar info to the bracelet for display. Path: My tab → Settings → Connected devices"
                    />
                  </Box>
                ) : (
                  <TableContainer component={Paper} elevation={0}
                    sx={{ mb: 4, border: "1px solid #e2e8f0", overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 520 }}>
                      <TableHead sx={{ bgcolor: "#f8fafc" }}>
                        <TableRow>
                          {["Permission Identifier", "Usage Scenarios", "Call Path"].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ verticalAlign: "top", fontSize: "0.78rem" }}>
                            <code>ACCESS_BACKGROUND_LOCATION</code><br />
                            <code>ACCESS_FINE_LOCATION</code><br />
                            <code>ACCESS_COARSE_LOCATION</code><br />
                            <code>BLUETOOTH</code><br />
                            <code>BLUETOOTH_ADMIN</code>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", fontSize: "0.8rem" }}>
                            The application needs the user's location to calculate running distance,
                            search for the BLE bracelet, locate in the background, and draw the activity trajectory.
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", fontSize: "0.8rem" }}>
                            App Home &rarr; "Sports" tab &rarr; Running
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ verticalAlign: "top", fontSize: "0.78rem" }}>
                            <code>READ_SMS</code><br />
                            <code>CALL_PHONE</code><br />
                            <code>ACCESS_NOTIFICATION_POLICY</code><br />
                            <code>WRITE_CONTACTS</code><br />
                            <code>RECEIVE_SMS</code>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", fontSize: "0.8rem" }}>
                            The application pushes phone calls, text messages, and notification bar content
                            to the bracelet for on-device display.
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", fontSize: "0.8rem" }}>
                            App home page &rarr; "My" tab &rarr; Settings &rarr; Connected devices
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Typography variant="h6" sx={{ fontWeight: 700, color: "#15803d", mb: 2, fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  Permission Classification — Full Reference
                </Typography>

                {isCompact ? (
                  <Box>
                    <PermRow label="Calendar" ids="READ_CALENDAR · WRITE_CALENDAR" why="Study plans, travel itineraries, birthday & meeting reminders." />
                    <PermRow label="Camera" ids="CAMERA" why="Photos, QR/barcode scanning, intelligent image recognition, profile pictures." />
                    <PermRow label="Contacts & Accounts" ids="READ_CONTACTS · WRITE_CONTACTS · GET_ACCOUNTS" why="Add friends from address book, backup contacts, phone book management on wearable." />
                    <PermRow label="Location" ids="ACCESS_FINE_LOCATION · ACCESS_COARSE_LOCATION · ACCESS_BACKGROUND_LOCATION" why="Activity trajectories (walking, cycling), sports tracking, discovering nearby Bluetooth devices." />
                    <PermRow label="Recording" ids="RECORD_AUDIO" why="Voice assistant, voice navigation, voice calls, chat." />
                    <PermRow label="Device Info & Phone" ids="READ_PHONE_STATE · READ/WRITE_CALL_LOG · CALL_PHONE · ANSWER_PHONE_CALLS · PROCESS_OUTGOING_CALLS" why="Call management, Caller ID on wearable, notification alerts, answering calls via watch." />
                    <PermRow label="Body Sensor" ids="BODY_SENSORS" why="Heart rate, SpO2, sleep, temperature telemetry from wearable device." />
                    <PermRow label="SMS" ids="SEND_SMS · RECEIVE_SMS · READ_SMS · RECEIVE_WAP_PUSH · RECEIVE_MMS · READ_CELL_BROADCASTS" why="Push SMS to bracelet display; SMS login verification code autofill." />
                    <PermRow label="Storage" ids="READ_EXTERNAL_STORAGE · WRITE_EXTERNAL_STORAGE" why="Download reports to device, open/save pictures and exported data." />
                    <PermRow label="Device Manager & Notifications" ids="BIND_DEVICE_ADMIN · BIND_NOTIFICATION_LISTENER_SERVICE" why="Push mobile notification bar info to wearable screen; remote device management." />
                    <PermRow label="Third Party Libraries" ids="Google Play Services (READ_PHONE_STATE) · TencentBugly (READ_PHONE_STATE)" why="Google sign-in, Maps routing; crash log capture and app stability troubleshooting." />
                  </Box>
                ) : (
                  <TableContainer component={Paper} elevation={0}
                    sx={{ border: "1px solid #e2e8f0", overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 560 }}>
                      <TableHead sx={{ bgcolor: "#f8fafc" }}>
                        <TableRow>
                          {["Classification", "Permission Definition", "Typical Scenarios"].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          ["Calendar", "READ_CALENDAR · WRITE_CALENDAR: Obtain / edit calendar data.", "Study plans, travel itineraries, birthday & meeting reminders."],
                          ["Camera", "CAMERA: Allow app to use camera.", "Photos, QR/barcode scanning, intelligent image recognition."],
                          ["Contacts & Accounts", "READ_CONTACTS · WRITE_CONTACTS · GET_ACCOUNTS", "Add friends from address book, backup contacts, phone book management on wearable."],
                          ["Location", "ACCESS_FINE_LOCATION · ACCESS_COARSE_LOCATION · ACCESS_BACKGROUND_LOCATION", "Activity trajectories, sports tracking, discovering nearby Bluetooth devices."],
                          ["Recording", "RECORD_AUDIO: Use microphone.", "Voice assistant, voice navigation, voice calls, chat."],
                          ["Device Info & Phone", "READ_PHONE_STATE · READ/WRITE_CALL_LOG · CALL_PHONE · ANSWER_PHONE_CALLS · PROCESS_OUTGOING_CALLS", "Call management, Caller ID on wearable, notification alerts, answering calls via watch."],
                          ["Body Sensor", "BODY_SENSORS: Access body measurement sensor data.", "Heart rate, SpO2, sleep, temperature telemetry from wearable."],
                          ["SMS", "SEND/RECEIVE/READ_SMS · RECEIVE_WAP_PUSH · RECEIVE_MMS · READ_CELL_BROADCASTS", "Push SMS to bracelet; SMS verification code autofill during login."],
                          ["Storage", "READ_EXTERNAL_STORAGE · WRITE_EXTERNAL_STORAGE", "Download reports to device, open/save pictures and exported data."],
                          ["Device Manager & Notifications", "BIND_DEVICE_ADMIN · BIND_NOTIFICATION_LISTENER_SERVICE", "Push notification bar info to wearable screen; remote device management."],
                          ["Third Party Libraries", "Google Play Services: device IDs, maps. TencentBugly: crash reporting (READ_PHONE_STATE).", "Google sign-in & Maps; crash log capture for app stability troubleshooting."],
                        ].map(([cls, def, scen]) => (
                          <TableRow key={cls}>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.8rem" }}>{cls}</TableCell>
                            <TableCell sx={{ fontSize: "0.78rem" }}><code>{def}</code></TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>{scen}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Box>
      )}

      {/* ── SECTION 2: EMED4U ──────────────────────────────────── */}
      {(activeView === "all" || activeView === "emed4u") && (
        <Box id="emed4u-section" sx={sectionBox}>
          <Paper elevation={2} sx={sectionPaper("#93c5fd", "#f8fafc")}>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <Box component="img" src={emed4uIcon} alt="Emed4u"
                sx={{ width: { xs: 44, sm: 56 }, height: { xs: 44, sm: 56 }, borderRadius: 2, boxShadow: 1, bgcolor: "#fff", p: 0.5, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={h4("#1d4ed8")}>Privacy Policy — Emed4u App & Telehealth Platform</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  Telemedicine, Online Consultations & Electronic Health Records (PHR)
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" sx={h6color("#1e40af")}>1. Document & Photo Picker Access</Typography>
            <Typography variant="p">
              When using Emed4u to upload medical prescriptions, lab reports, or profile pictures:
              <ul>
                <li><strong>Privacy-Preserving Pickers:</strong> The app uses the standard <strong>Android System Photo Picker</strong> and <strong>Storage Access Framework (SAF)</strong>. You manually select only the specific file you wish to share. The app never accesses files outside your explicit selection.</li>
                <li><strong>No Broad File Access:</strong> Emed4u does not request or use <code>MANAGE_EXTERNAL_STORAGE</code> permissions.</li>
                <li><strong>App-Specific Downloads:</strong> Invoices, receipts, or appointment summaries are saved to isolated app-specific directories (<code>getExternalFilesDir</code>) or the system <code>DownloadManager</code>.</li>
              </ul>
            </Typography>

            <Typography variant="h6" sx={h6color("#1e40af")}>2. Android Runtime Permissions & Purpose</Typography>
            <TableContainer component={Paper} elevation={0}
              sx={{ my: 2, border: "1px solid #e0e0e0", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <Table size="small" sx={{ minWidth: { xs: 480, sm: "unset" } }}>
                <TableHead sx={{ bgcolor: "#eff6ff" }}>
                  <TableRow>
                    {["Permission", "Android Manifest Identifier", "Purpose & User Benefit"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ["Camera & Microphone", "CAMERA, RECORD_AUDIO", "Requested only when you participate in a live encrypted telemedicine video consultation with a verified doctor."],
                    ["Location (Optional)", "ACCESS_FINE_LOCATION, ACCESS_COARSE_LOCATION", "Used with your consent to discover nearby healthcare providers, clinics, and diagnostic centers."],
                    ["Notifications", "POST_NOTIFICATIONS", "Sends timely appointment reminders, telemedicine call alerts, and digital prescription updates."],
                    ["Document Selection", "Storage Access Framework (SAF) / Photo Picker", "Allows user-controlled selection of medical records and prescription documents for upload."],
                  ].map(([perm, id, why]) => (
                    <TableRow key={perm}>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{perm}</TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.72rem", sm: "0.8rem" } }}><code>{id}</code></TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{why}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" sx={h6color("#1e40af")}>3. Health Records Security (PHR)</Typography>
            <Typography variant="p">
              All electronic medical records, consultation notes, and personal identifiers are encrypted in transit
              via TLS/HTTPS and encrypted at rest on secure cloud servers. Personal and medical data is never sold
              or rented to third parties.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* ── SECTION 3: ACCOUNT & DATA DELETION ─────────────────── */}
      {(activeView === "all" || activeView === "deletion") && (
        <Box id="account-deletion-section" sx={sectionBox}>
          <Paper elevation={2} sx={sectionPaper("#cbd5e1", "#ffffff")}>
            <Typography sx={{ fontWeight: 700, color: "#334155", mb: 2, fontSize: { xs: "1.3rem", sm: "1.6rem", md: "2rem" } }}>
              Account & Data Deletion Policy
            </Typography>

            <Typography variant="p">
              Users of Emed4u and Emed(Koti)Devices have complete control over their personal data and may
              request account and data deletion at any time.
            </Typography>

            <Typography variant="h6" sx={h6color("#334155")}>How to Request Account Deletion</Typography>
            <Typography variant="p">
              <ol>
                <li>
                  <strong>In-App Self-Service:</strong> In the Emed4u app or web portal, go to{" "}
                  <strong>Settings</strong> &rarr; <strong>Account Settings</strong> &rarr;{" "}
                  <strong>Delete My Account</strong> and follow the confirmation prompts.
                </li>
                <li>
                  <strong>Email Request:</strong> Send an email from your registered address to{" "}
                  <strong>info@emed4u.com</strong> with the subject{" "}
                  <em>"Account & Data Deletion Request"</em> including your registered phone number or User ID.
                </li>
              </ol>
            </Typography>

            <Typography variant="h6" sx={h6color("#334155")}>Deletion Scope & Timelines</Typography>
            <Typography variant="p">
              Upon verified request, your login credentials, profile data, device pairing records, and health
              telemetry are permanently removed from active databases within <strong>30 days</strong>, except
              for records that must be retained under statutory medical documentation or financial auditing regulations.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* ── SECTION 4: GENERAL LEGAL TERMS ─────────────────────── */}
      {(activeView === "all" || activeView === "general") && (
        <Box id="general-legal-section" sx={{ width: "100%", pt: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, textAlign: "left", fontSize: { xs: "1.3rem", sm: "1.6rem", md: "2rem" } }}>
            General Legal Terms & Data Protection Policy
          </Typography>

          <Typography variant="p">
            Emed4u website and app ("Website" / "App" respectively), respects your privacy, and seeks to comply
            with applicable legal requirements in respect of data collection, processing and transfer. This privacy
            notice describes the information about you that EMED4U collects through this website, how that
            information is used, maintained, shared, protected and how you can update it. It also applies to all
            personal data received by EMED4U from the European Economic Area ("EEA") in any format, including
            electronic or paper.
          </Typography>

          <ol className="multilevel">
            <li>
              <Typography variant="h6"><strong>{"WHY THIS PRIVACY POLICY?"}</strong></Typography>
              <br />
              <Typography variant="p">
                This Privacy Policy is published in compliance with, inter alia:
                <br />
                <ul>
                  <li>Section 43A of the Information Technology Act, 2000;</li>
                  <li>Regulation 4 of the Information Technology (Reasonable Security Practices and Procedures and</li>
                  <li>Sensitive Personal Information) Rules, 2011 (the "SPI Rules");</li>
                  <li>Regulation 3(1) of the Information Technology (Intermediaries Guidelines) Rules, 2011.</li>
                  <li>This Privacy Policy states the following:</li>
                  <li>The type of information collected from the Users, including Personal Information and Sensitive Personal Data or Information relating to an individual;</li>
                  <li>The purpose, means and modes of collection, usage, processing, retention and destruction of such information; and</li>
                  <li>How and to whom Emed4u will disclose such information.</li>
                </ul>
              </Typography>
            </li>
            <li>
              <Typography variant="h6"><strong>{"PERSONAL DATA THAT WE COLLECT"}</strong></Typography>
              <br />
              <Typography variant="p">
                Personal data refers to data that lets EMED4U know the specifics of who you are and which may be used to identify, contact or locate you but not limited to the following:
                <br />
                <ul>
                  <li>Contact information: Name, Address, Contact details, Email ID, Phone Number;</li>
                  <li>Demographic information: Gender, Age, Date of Birth; Marital Status, Nationality.</li>
                  <li>Data regarding your usage of the services such as search history and history of the appointments made by you through the use of Services;</li>
                  <li>Any other detail relating to the above as voluntarily provided to Emed4u by you, for providing value added service;</li>
                  <li>Medical records and history which you voluntarily provide or is generated on usage of any of the services availed by you from any entity in the Emed4u;</li>
                  <li>Physical, physiological and mental health condition which you voluntarily provide or is generated on usage of our services through the app/website;</li>
                  <li>Data regarding your medical qualifications, registrations and certifications;</li>
                  <li>Any additional information that you voluntarily choose to provide to Emed4u 24*7 through any mode of communication or during any interaction.</li>
                </ul>
              </Typography>
            </li>
            <li>
              <Typography variant="h6"><strong>{"WHERE DO WE COLLECT YOUR DATA FROM?"}</strong></Typography>
              <Typography variant="p">
                For end users:
                <br />
                <ul>
                  <li>Any information that you voluntarily choose to provide to us through app, website, email, during interaction with us on call or chat and other modes of communication; Information that we collect from healthcare service provider (HSPs) such as doctors, hospitals, etc. in the Emed4u, to whom you have permitted the sharing of your personal information;</li>
                  <li>Data you have provided to any group company of the Company, affiliates, associates, subsidiary, holding company of the Company, associates and subsidiaries of holding company of the Company, to whom you have given consent for sharing of such information.</li>
                </ul>
                For doctors:
                <br />
                <ul>
                  <li>Before onboarding you, we may request information regarding your qualifications, experience, public profile, and representations made by you to us;</li>
                  <li>During your usage of the App / Website, we may collect information based on your use of the Services;</li>
                  <li>We may collect other information that you voluntarily choose to provide to us through app, website, email, during interaction with us on call or chat and other modes of communication;</li>
                  <li>We may collect information from any group company, affiliates, associates, subsidiary, holding company of the Company, associates and subsidiaries of holding company of the Company to whom you have given consent for sharing of information for availing value added service.</li>
                </ul>
                You hereby consent to the collection of such information by Emed4u.
                Without prejudice to the generality of the above, information collected by us from you may include (but is not limited to) the following:
                <ul>
                  <li>contact data (such as your email address and phone number);</li>
                  <li>demographic data (such as your gender, your date of birth and your pin code);</li>
                  <li>data regarding your usage of the services and history of the appointments made by or with you through the use of Services;</li>
                  <li>insurance data (such as your insurance carrier and insurance plan);</li>
                  <li>other information that you voluntarily choose to provide to us (such as information shared by you with us through emails or letters).</li>
                </ul>
                The information collected from you by Emed4u may constitute 'personal information' or 'sensitive personal data or information' under the SPI Rules.
                <br /><br />
                "Personal Information" is defined under the SPI Rules to mean any information that relates to a natural person, which, either directly or indirectly, in combination with other information available or likely to be available to a body corporate, is capable of identifying such person.
                <br /><br />
                The SPI Rules further define "Sensitive Personal Data or Information" of a person to mean personal information about that person relating to:
                <ul>
                  <li>passwords;</li>
                  <li>financial information such as bank accounts, credit and debit card details or other payment instrument details;</li>
                  <li>physical, physiological and mental health condition;</li>
                  <li>sexual orientation;</li>
                  <li>medical records and history;</li>
                  <li>biometric information;</li>
                  <li>information received by body corporate under lawful contract or otherwise;</li>
                  <li>visitor details as provided at the time of registration or thereafter; and</li>
                  <li>call data records.</li>
                </ul>
                <br />
                Emed4u will be free to use, collect and disclose information that is freely available in the public domain without your consent.
                <br /><br />
                EMED4U websites may contain links to other websites. EMED4U shall not be responsible for the privacy notices or the content of such websites.
              </Typography>
            </li>
            <li>
              <Typography variant="h6"><strong>{"HOW WE USE YOUR PERSONAL DATA"}</strong></Typography>
              <Typography variant="p">
                We can only use your personal data if we have a proper reason for doing so. We only use your data for one or more of these reasons:
                <br /><br />
                General (end users and doctors):
                <br /><br />
                <ul>
                  <li>Your registration for the purpose of receiving our Services, identification, communication, notification and for fulfilment of the Terms and Conditions;</li>
                  <li>Analysis, research, training and disclosure (where required) to its affiliates, agents and government authorities.</li>
                  <li>Offering you personalized Services and targeted advertisements of various healthcare and wellness plans and offering you customised health insights;</li>
                  <li>Improvement of our products and services;</li>
                  <li>Research and analysis for the development of commercial solutions;</li>
                  <li>Addressing your requests, queries and complaints, in any, pertaining to our Services; other customer care related activities;</li>
                  <li>Investigating, enforcing, and resolving disputes;</li>
                  <li>For the purpose of contacting you to provide you with information on new Services and offers, taking feedback, assisting you with completion of transactions or other issues relating to the use of Services;</li>
                  <li>To send notices, communications, alerts, new offers relevant to use of the Services offered by Emed4u 24*7 on Website/app;</li>
                  <li>Carrying out our obligations in relation to any agreement with our business partners or contractors;</li>
                  <li>Technical administration and customization of Website, and other general administrative and business purposes;</li>
                  <li>To send you information about special promotions or offers (either offered by Emed4u 24*7 or by its business partners), new features or products both of the Apollo as well as third-party offers or products with whom Emed4u 24*7 has a tie-up;</li>
                </ul>
                <br />
                For end users only:
                <br /><br />
                <ul>
                  <li>Creation and maintenance of health records in electronic form in the Personal Health Record (PHR) database;</li>
                  <li>For sharing with your chosen HSP in the Emed4u like doctors, hospitals, diagnostic centres, chemists who may provide you services under the Emed4u 24*7 Platform.</li>
                  <li>Processing any orders you may place using our Services;</li>
                  <li>For doctors only:</li>
                  <li>For verifying your professional credentials and any representations you have made to us;</li>
                  <li>For processing any payments made to you;</li>
                  <li>For providing recommendations to end users based on your expertise and specialisations;</li>
                  <li>For providing any other service to you.</li>
                  <li>To fulfil a contract we have with you, or</li>
                  <li>If we have a legal duty to use your data for a particular reason, or</li>
                  <li>When we get your consent to use it, or</li>
                  <li>When it is in our legitimate interests which are our business or commercial reasons for using your data, but even so, we will not unfairly put our legitimate interests above what is best for you.</li>
                </ul>
                <br /><br />
                <strong>Marketing and events:</strong> We use personal information to deliver marketing and event communications to you across various platforms, such as email, telephone, text messaging, direct mail, and online.
                <br /><br />
                <strong>Legal obligations:</strong> We may be required to use and retain personal information for legal and compliance reasons, such as the prevention, detection, or investigation of a crime; loss prevention; or fraud.
                <br /><br />
                <strong>We endeavour to collect only such information that is reasonably necessary to perform services for you or to respond to your inquiries. You are responsible for ensuring that the information you provide is accurate, complete and current.</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="h6"><strong>{"DATA STORAGE & RETENTION"}</strong></Typography>
              <Typography variant="p">
                We store your personal information in accordance with applicable laws, which means we keep your data for as long as necessary to provide you with our Services or as may be required under any law. We shall store your personal information for lawful purposes only. We keep de-identified data for research and statistical purposes for a longer period.
                <br /><br />
                If you close your account, we have no obligation to retain your data, and we may delete any or all of your data without liability. However, we may retain data related to you if we believe it may be necessary to prevent fraud or future abuse, or if required by law, or for other legitimate purposes.
              </Typography>
            </li>
            <br />
            <li>
              <Typography variant="h6"><strong>{"PRIVACY STATEMENTS & USER RIGHTS"}</strong></Typography>
              <Typography variant="p">
                All the statements in this Privacy Policy apply to all End-Users and Practitioners. You are responsible for maintaining the accuracy of the information you submit to us. If your personal information changes, you may correct, delete inaccuracies, or amend information by accessing your account profile or contacting info@emed4u.com.
              </Typography>
            </li>
            <li>
              <Typography variant="h6"><strong>{"WHEN WE SHARE PERSONAL DATA"}</strong></Typography>
              <Typography variant="p">
                EMED4U shares or discloses personal data when necessary to provide services or conduct our business operations with appropriate contractual safeguards. We do not sell or rent personal data to third parties.
              </Typography>
            </li>
            <li>
              <Typography variant="h6"><strong>{"WHERE WE STORE AND PROCESS PERSONAL DATA"}</strong></Typography>
              <Typography variant="p">
                EMED4U processes data on secure cloud servers complying with standard encryption and international data protection practices.
              </Typography>
            </li>
            <li>
              <Typography variant="h6"><strong>{"HOW WE SECURE PERSONAL DATA"}</strong></Typography>
              <Typography variant="p">
                EMED4U employs reasonable technological, physical, and operational security procedures intended to protect personal data from unauthorized access, disclosure, loss, or destruction.
              </Typography>
            </li>
            <li>
              <Typography variant="h6"><strong>{"COOKIES"}</strong></Typography>
              <Typography variant="p">
                We use cookies on our web portal to enhance site functionality and user experience. Users can manage cookie preferences in browser settings.
              </Typography>
            </li>
            <li>
              <Typography variant="h6"><strong>{"CHILDREN UNDER 13 YEARS OF AGE"}</strong></Typography>
              <Typography variant="p">
                We do not proactively collect personal information directly from children under 13 without parental consent.
              </Typography>
            </li>
          </ol>
        </Box>
      )}

      {/* ── COMPANY & CONTACT INFO (always at the bottom) ──────── */}
      <Box sx={{ width: "100%", mt: 5 }}>
        <Paper elevation={0}
          sx={{ p: { xs: 2, sm: 3.5 }, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b", mb: 1, fontSize: { xs: "1.1rem", sm: "1.4rem" } }}>
            Company & Contact Information
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mb: 2, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
            For questions, data requests, or privacy inquiries regarding Emed4u or Emed(Koti)Devices:
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body1" sx={{ mt: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            <strong>Company Name:</strong> Emed4u.com Pvt Ltd (Emed4u Private Limited)
          </Typography>
          <Typography variant="body1" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            <strong>Email:</strong>{" "}
            <a href="mailto:info@emed4u.com">info@emed4u.com</a>
          </Typography>
          <Typography variant="body1" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            <strong>Registered Address / Location:</strong> 261 Shiv Nagar Colony, Opposite Jwala Estate, Amritsar, Punjab 143001, India
          </Typography>
          <Typography variant="body1" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            <strong>Official Website:</strong>{" "}
            <a href="https://emed4u.com" target="_blank" rel="noopener noreferrer">https://emed4u.com</a>
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: "text.secondary", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
            <strong>Effective Date:</strong> August 26, 2026
          </Typography>
        </Paper>
      </Box>
    </StyledContainer>
  );
}

export default PrivacyPolicy;
